import React, { useCallback, useEffect, useState } from "react";
import { Building, Loader2, Pencil, Plus, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { DataTable } from "@/components/common/DataTable";
import { EmptyState, LoadingRows } from "@/components/common/EmptyState";
import { StatusBadge } from "@/components/common/StatusBadge";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Field } from "@/pages/crm/LeadForm";

const blank = {
  name: "", company: "", contact_person: "", email: "", phone: "", gstin: "", address: "",
  bank_details: "", payment_terms: "", categories: [], notes: "", active: true,
};

export const SuppliersPanel = () => {
  const [rows, setRows] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [del, setDel] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, c] = await Promise.all([api.get("/suppliers", { params: { q } }), api.get("/categories")]);
      setRows(s.data); setCats(c.data);
    } finally { setLoading(false); }
  }, [q]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editing) await api.patch(`/suppliers/${editing.id}`, form);
      else await api.post("/suppliers", form);
      toast.success(`Supplier ${editing ? "updated" : "added"}`);
      setOpen(false); load();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const toggleCat = (name) => setForm((f) => ({
    ...f, categories: f.categories.includes(name) ? f.categories.filter((c) => c !== name) : [...f.categories, name],
  }));

  const columns = [
    {
      key: "name", label: "Supplier",
      render: (r) => (
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold">{r.name}</p>
          <p className="truncate text-xs text-muted-foreground">{r.company}</p>
        </div>
      ),
    },
    { key: "contact_person", label: "Contact", render: (r) => <div><p className="text-sm">{r.contact_person || "\u2014"}</p><p className="text-xs text-muted-foreground">{r.phone}</p></div> },
    { key: "email", label: "Email", render: (r) => <span className="text-sm text-muted-foreground">{r.email}</span>, hideMobile: true },
    { key: "payment_terms", label: "Terms", render: (r) => <span className="text-sm text-muted-foreground">{r.payment_terms || "\u2014"}</span>, hideMobile: true },
    { key: "active", label: "Status", render: (r) => <StatusBadge status={r.active ? "Active" : "Inactive"} /> },
    {
      key: "actions", label: "", align: "right",
      render: (r) => (
        <div className="flex justify-end gap-1.5" onClick={(e) => e.stopPropagation()}>
          <Button size="iconSm" variant="outline" onClick={() => { setEditing(r); setForm({ ...blank, ...r }); setOpen(true); }}>
            <Pencil className="h-4 w-4" />
          </Button>
          <Button size="iconSm" variant="outline" onClick={() => setDel(r)}>
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" placeholder="Search suppliers" />
        </div>
        <Button onClick={() => { setEditing(null); setForm(blank); setOpen(true); }} data-testid="new-supplier">
          <Plus className="h-4 w-4" /> Add supplier
        </Button>
      </div>

      {loading ? <LoadingRows /> : (
        <DataTable columns={columns} rows={rows}
          empty={<EmptyState icon={Building} title="No suppliers yet" description="Add suppliers so they can be selected while raising purchase orders and stock entries." />} />
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto scroll-slim">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">{editing ? `Edit ${editing.name}` : "Add supplier"}</DialogTitle>
            <DialogDescription>Suppliers appear as options in purchase orders and stock entries.</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Supplier name" required>
                <Input required value={form.name} data-testid="supplier-name" onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </Field>
              <Field label="Company name"><Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} /></Field>
              <Field label="Contact person"><Input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} /></Field>
              <Field label="Email"><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
              <Field label="Phone"><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
              <Field label="GST / Tax number"><Input value={form.gstin} onChange={(e) => setForm({ ...form, gstin: e.target.value })} /></Field>
              <Field label="Payment terms"><Input value={form.payment_terms} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })} /></Field>
              <div className="flex items-end pb-2">
                <label className="flex items-center gap-3 text-sm">
                  <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /> Active supplier
                </label>
              </div>
            </div>
            <Field label="Billing address">
              <Textarea rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
            </Field>
            <Field label="Bank details">
              <Textarea rows={2} value={form.bank_details} onChange={(e) => setForm({ ...form, bank_details: e.target.value })} />
            </Field>
            <Field label="Product categories supplied">
              <div className="flex flex-wrap gap-2">
                {cats.map((c) => (
                  <button key={c.id} type="button" onClick={() => toggleCat(c.name)}
                    className={`rounded-full border px-3 py-1 text-xs transition-colors ${
                      form.categories.includes(c.name)
                        ? "border-primary bg-primary-soft text-primary"
                        : "border-border hover:border-primary/40"}`}>
                    {c.name}
                  </button>
                ))}
              </div>
            </Field>
            <Field label="Notes">
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </Field>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={busy} data-testid="supplier-save">
                {busy && <Loader2 className="h-4 w-4 animate-spin" />}{editing ? "Save changes" : "Add supplier"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={!!del} onOpenChange={(v) => !v && setDel(null)}
        title="Delete supplier?" description={`${del?.name} will be removed from the supplier list.`}
        confirmLabel="Delete" destructive
        onConfirm={async () => { await api.delete(`/suppliers/${del.id}`); toast.success("Supplier deleted"); setDel(null); load(); }} />
    </div>
  );
};
